/**
 * "Your plan, in plain words" — the Support at Home module's interpretation layer.
 *
 * REWRITTEN 2026-08-25 after the owner caught the first cut red-handed: with only a partner's
 * aged-care plan linked (no manual levers), it said "a set of adjustments to how the assets are
 * held" and then repeated the Financial Impact panel's numbers — interpreting nothing, and
 * ignoring the single biggest driver on the page. The rules this version follows:
 *   • the linked aged-care scenario is a first-class part of the story — who moves into care,
 *     the RAD paid (exempt for the pension), the fees hitting cashflow, and the
 *     illness-separated reassessment that usually explains most of the pension jump;
 *   • every lever gets its means-test meaning, with its amount;
 *   • the contribution-rate movement is interpreted (the rates panel shows was/now — this card
 *     says WHY they fell);
 *   • nothing renders when there is nothing to interpret — no filler sentences, ever.
 *
 * CALCULATION DISCIPLINE — every figure is quoted from the section's own state: the before/after
 * snapshots and contribution data its panels display, and the linked strategy's own saved
 * outputs. Nothing is re-derived here.
 */
import { EngineAnnuityTerms } from '../../components/strategy/AnnuityTerms.tsx';
import { EquityReleaseTerms } from '../../components/strategy/EquityReleaseTerms.tsx';
import React from 'react';
import { capitalReturnedWords } from '../../components/strategy/annuityWords.ts';
import { formatCurrency } from '../../utils/index.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, ShiftRow } from '../../components/narrative/PlainWords.tsx';
import { PlanPiecesCard, type PlanPiece } from '../../components/narrative/PlanPieces.tsx';
import { GIFTING_LIMIT_ANNUAL } from '../../constants/index.ts';

export interface SahIntegrationSummary {
    /** The saved aged care strategy's display name. */
    name: string;
    /** Who moves into residential care under it. */
    personName: string;
    annualFees: number;
    radPaid: number;
    /** Whether the linked scenario makes the household an illness-separated couple. */
    illnessSeparated: boolean;
}

interface ContributionRates {
    independence?: number;
    everydayLiving?: number;
}

interface SahPlanRecapProps {
    before: { socialSecurity?: number; cashFlow?: number } | null;
    after: { socialSecurity?: number; cashFlow?: number } | null;
    /** Annual means-tested Support at Home contribution, before and with the plan. */
    contributionBefore: number;
    contributionAfter: number;
    ratesBefore?: ContributionRates | null;
    ratesAfter?: ContributionRates | null;
    integration?: SahIntegrationSummary | null;
    gifting: number;
    funeralBonds: number;
    /** 'none' | 'downsize' | 'renovate' | 'upgrade' | 'reverseMortgage' | 'heas' */
    propertyStrategy: string;
    /** Downsize only: sale money not yet reallocated - sits in the bank, counted and deemed. */
    downsizeUnallocated?: number;
    reverseMortgage: boolean;
    heas: boolean;
    annuitySelected: boolean;
    /**
     * The terms the annuity is actually built on — owner, residual capital, reversionary,
     * indexation.
     *
     * Owner-raised, 2026-09-01: the recap could say "buying a lifetime annuity" of a six-figure
     * commitment without saying whose it was, whether anything came back, or — for a couple — that
     * the income stops on the owner's death. Optional, because a plan can be recapped before the
     * annuity is configured; the disclosure simply does not render until there is something to
     * disclose.
     */
    annuityTerms?: Record<string, unknown> | null;
    /**
     * The equity-release loan this plan takes, where it takes one, with what the projection says it
     * grows to.
     *
     * The lever phrase above ("borrowing against the home ... the interest compounds") says the right
     * thing and says it in passing. A reverse mortgage makes NO repayments, so nothing about it
     * reaches any cashflow figure — its whole cost is the closing balance, and a client cannot weigh
     * a compounding debt from a clause (owner, 2026-09-01).
     */
    equityRelease?: Record<string, unknown> | null;
    /** Only for naming who owns the annuity. Everything else here comes from the figures passed in. */
    factFind?: any;
    hasStrategies: boolean;
}

const PROPERTY_PHRASES: Record<string, { phrase: string; pensionDriver: string }> = {
    downsize: {
        phrase: 'downsizing the home, freeing up money from the sale',
        pensionDriver: 'the money the downsize frees up becomes countable where the home was exempt',
    },
    renovate: {
        phrase: 'renovating — moving countable savings into the exempt home',
        pensionDriver: 'renovation money moves from counted savings into the exempt home, so Centrelink counts less',
    },
    upgrade: {
        phrase: 'upgrading to a dearer home — moving countable savings into the exempt home',
        pensionDriver: 'the upgrade moves counted savings into the exempt home, so Centrelink counts less',
    },
    reverseMortgage: {
        phrase: 'borrowing against the home (nothing repaid along the way, so the interest compounds)',
        pensionDriver: 'borrowed money that sits in the bank is counted and deemed',
    },
    heas: {
        phrase: 'drawing the government Home Equity Access Scheme against the home',
        pensionDriver: 'the fortnightly HEAS payments themselves do not reduce the pension',
    },
};

const SahPlanRecap = ({
    before,
    after,
    contributionBefore,
    contributionAfter,
    ratesBefore = null,
    ratesAfter = null,
    integration = null,
    gifting,
    funeralBonds,
    propertyStrategy,
    downsizeUnallocated = 0,
    reverseMortgage,
    heas,
    annuitySelected,
    annuityTerms = null,
    equityRelease = null,
    factFind = null,
    hasStrategies,
}: SahPlanRecapProps) => {
    // Nothing to interpret → nothing rendered. No filler sentences, ever.
    if ((!hasStrategies && !integration) || !before || !after) return null;

    const pensionBefore = before.socialSecurity ?? 0;
    const pensionAfter = after.socialSecurity ?? 0;
    const pensionDelta = Math.round(pensionAfter) - Math.round(pensionBefore);
    const contributionDelta = Math.round(contributionAfter) - Math.round(contributionBefore);
    const giftExcess = Math.max(0, gifting - GIFTING_LIMIT_ANNUAL);

    const pct = (v: number | undefined | null) => (v == null ? null : `${(v * 100).toFixed(1)}%`);
    const ratesMoved =
        ratesBefore &&
        ratesAfter &&
        (Math.round(((ratesBefore.independence ?? 0) - (ratesAfter.independence ?? 0)) * 1000) !== 0 ||
            Math.round(((ratesBefore.everydayLiving ?? 0) - (ratesAfter.everydayLiving ?? 0)) * 1000) !== 0);
    const ratesFell =
        ratesMoved &&
        (ratesAfter!.independence ?? 0) <= (ratesBefore!.independence ?? 0) &&
        (ratesAfter!.everydayLiving ?? 0) <= (ratesBefore!.everydayLiving ?? 0);

    // ---- The levers actually in play, each with its means-test meaning ----
    const levers: React.ReactNode[] = [];
    if (gifting > 0) {
        levers.push(
            <React.Fragment key="gift">
                gifting {formatCurrency(gifting)}
                {giftExcess > 0 && (
                    <>
                        {' '}— the {formatCurrency(giftExcess)} above the yearly free area still counts for five years
                        under the <JargonTerm term="deprivation" /> rules
                    </>
                )}
            </React.Fragment>,
        );
    }
    if (funeralBonds > 0) {
        levers.push(
            <React.Fragment key="fb">
                putting {formatCurrency(funeralBonds)} into a funeral bond (exempt within the limit)
            </React.Fragment>,
        );
    }
    if (propertyStrategy !== 'none' && PROPERTY_PHRASES[propertyStrategy]) {
        levers.push(<React.Fragment key="prop">{PROPERTY_PHRASES[propertyStrategy].phrase}</React.Fragment>);
    }
    if (reverseMortgage && propertyStrategy !== 'reverseMortgage')
        levers.push(<React.Fragment key="rm">{PROPERTY_PHRASES.reverseMortgage.phrase}</React.Fragment>);
    if (heas && propertyStrategy !== 'heas')
        levers.push(<React.Fragment key="heas">{PROPERTY_PHRASES.heas.phrase}</React.Fragment>);
    if (annuitySelected) {
        levers.push(
            <React.Fragment key="ann">
                buying a lifetime annuity — Centrelink counts only 60% of what it costs (30% later on)
            </React.Fragment>,
        );
    }

    // ---- Driver-named whys ----
    const pensionDrivers: string[] = [];
    if (integration) {
        if (integration.illnessSeparated)
            pensionDrivers.push(
                `with ${integration.personName} in residential care, Centrelink assesses the household as an illness-separated couple — each paid at the higher single rate, with more generous thresholds`,
            );
        if (integration.radPaid > 0)
            pensionDrivers.push(
                `the ${formatCurrency(integration.radPaid)} paid as a RAD is exempt from the pension assets test — savings Centrelink was counting became a deposit it doesn't`,
            );
    }
    if (propertyStrategy === 'downsize' && downsizeUnallocated > 0) {
        pensionDrivers.push(
            `${formatCurrency(downsizeUnallocated)} of sale money sits unallocated in the bank ` +
                `— where the home was exempt, that money is counted and deemed ` +
                `(allocate it in the downsize section and this reverses)`,
        );
    } else if (propertyStrategy !== 'none' && PROPERTY_PHRASES[propertyStrategy]) {
        pensionDrivers.push(PROPERTY_PHRASES[propertyStrategy].pensionDriver);
    }
    // The panel's sharpest chain, kept: no pension at all means the household is assessed as
    // fully self-funded, which carries the HIGHEST contribution rates.
    const pensionLost = pensionBefore > 0 && pensionAfter <= 0;
    if (pensionLost) {
        pensionDrivers.push(
            'the changes push past the pension cut-off entirely, so the Age Pension stops ' +
                '— and with no pension, the household is assessed as fully self-funded, which is why the contribution rates jump to their highest',
        );
    }
    if (funeralBonds > 0) pensionDrivers.push(`the funeral bond is exempt within the limit`);
    if (annuitySelected) pensionDrivers.push(`only 60% of the annuity purchase is counted in the assets test`);
    if (gifting > 0 && giftExcess === 0)
        pensionDrivers.push(`the gifts sit inside the free areas, so they stop being counted immediately`);
    if (giftExcess > 0)
        pensionDrivers.push(`gifts above the free area keep counting for five years, so they help later, not now`);
    const pensionWhy =
        pensionDrivers.length > 0
            ? `What moves the pension: ${pensionDrivers.join('; ')}. The Financial Impact panel shows the same before-and-after.`
            : `The moves in this plan don't change what Centrelink counts by enough to shift the pension.`;

    const contributionWhy =
        `Support at Home contributions are means-tested like the pension: clinical care is always free, and the rates for independence and everyday-living services rise with assessable income and assets.` +
        (ratesMoved
            ? ` This plan ${ratesFell ? 'lowers' : 'changes'} what the assessment sees, so the rates move${
                  pct(ratesBefore?.independence) && pct(ratesAfter?.independence)
                      ? `: independence ${pct(ratesBefore?.independence)} → ${pct(ratesAfter?.independence)}, everyday living ${pct(ratesBefore?.everydayLiving)} → ${pct(ratesAfter?.everydayLiving)}`
                      : ''
              } — and the yearly contribution follows.`
            : contributionDelta === 0
              ? ` This plan doesn't change the assessment enough to move the rates.`
              : ` The same moves that change the pension change what has to be paid towards care.`);

    const cashflowDrivers: string[] = [];
    if (integration && integration.annualFees > 0)
        cashflowDrivers.push(`${integration.personName}'s aged care fees of ${formatCurrency(integration.annualFees)} a year start`);
    if (contributionDelta !== 0)
        cashflowDrivers.push(
            `the Support at Home contribution moves from ${formatCurrency(contributionBefore)} to ${formatCurrency(contributionAfter)} a year`,
        );
    if (pensionDelta !== 0)
        cashflowDrivers.push(
            `the Age Pension is ${pensionDelta > 0 ? 'higher' : 'lower'} (${formatCurrency(pensionBefore)} → ${formatCurrency(pensionAfter)} a year)`,
        );
    if (reverseMortgage || heas || propertyStrategy === 'reverseMortgage' || propertyStrategy === 'heas')
        cashflowDrivers.push('borrowed money adds spendable cash without selling anything');
    const cashflowWhy =
        `A full year of money in minus money out.` +
        (cashflowDrivers.length > 0 ? ` What changes it: ${cashflowDrivers.join('; ')}.` : '');

    const planPieces: PlanPiece[] = [];
    if (integration) planPieces.push({ key: 'care', name: `${integration.personName} moves into residential care`, amount: integration.radPaid > 0 ? integration.radPaid : undefined, what: `${integration.radPaid > 0 ? 'The deposit paid for the room is not counted in the assets test. ' : ''}${integration.illnessSeparated ? 'With one of you in care, Centrelink pays each of you at the higher single rate.' : 'The care fees are paid alongside the Support at Home contribution.'}`, glossaryTerm: integration.illnessSeparated ? 'illness separated' : 'rad' });
    if (propertyStrategy === 'downsize') planPieces.push({ key: 'downsize', name: 'Sell the home and buy a smaller one', what: 'The new home stays exempt as the principal residence. Whatever is freed up and not put into it becomes money Centrelink counts and deems.', glossaryTerm: 'deeming' });
    if (propertyStrategy === 'renovate') planPieces.push({ key: 'renovate', name: 'Spend on the home', what: 'Savings that were counted go into the home, which is exempt — so less is counted and the home is fit to stay in.' });
    if (propertyStrategy === 'upgrade') planPieces.push({ key: 'upgrade', name: 'Move to a dearer home', what: 'Counted savings go into a home that is exempt, so less is counted for the Age Pension and the care contribution.' });
    if (reverseMortgage || propertyStrategy === 'reverseMortgage') planPieces.push({ key: 'rm', name: 'Reverse mortgage', amount: Math.max(0, Number(equityRelease?.loanAmount) || 0) || undefined, what: 'A loan against the home with no repayments. The interest is added to the loan and the whole amount is repaid from the estate when the home is sold. Not counted as an asset.', glossaryTerm: 'reverse mortgage' });
    if (heas || propertyStrategy === 'heas') planPieces.push({ key: 'heas', name: 'Home Equity Access Scheme', what: 'A government loan against the home, paid fortnightly or as a lump sum, at a lower rate than a bank charges. The debt is repaid from the estate.', glossaryTerm: 'heas' });
    if (annuitySelected) {
        const residual = Number((annuityTerms as { residual?: unknown } | null)?.residual);
        const t = annuityTerms as { purchaseAmount?: unknown; capitalAccessPeriodYears?: unknown; lifeExpectancySexKnown?: unknown } | null;
        planPieces.push({ key: 'annuity', name: 'Lifetime annuity', amount: Math.max(0, Number(t?.purchaseAmount) || 0) || undefined, what: `A lump sum handed to an insurer in return for a guaranteed income for life. Only 60% of it counts in the assets test. ${Number.isFinite(residual) && residual > 0 ? capitalReturnedWords({ residualPct: Math.round(residual * 100), periodYears: t?.capitalAccessPeriodYears != null ? Number(t.capitalAccessPeriodYears) || null : null, sexKnown: t?.lifeExpectancySexKnown == null ? null : !!t.lifeExpectancySexKnown }) : 'Nothing of the purchase price comes back on death.'}`, glossaryTerm: 'lifetime annuity' });
    }
    if (gifting > 0) planPieces.push({ key: 'gift', name: 'Gift to family', amount: gifting, what: 'Money given away. Up to $10,000 a year ($30,000 over five years) leaves the means tests straight away; anything above that is still counted for five years.', glossaryTerm: 'gifting' });
    if (funeralBonds > 0) planPieces.push({ key: 'bond', name: 'Funeral bond', amount: funeralBonds, what: 'Money set aside for a funeral with a friendly society or insurer. Within the limit it is exempt from the assets test, and it can only be used for the funeral.', glossaryTerm: 'funeral bond' });

    return (
        <>
        <PlainWordsCard eyebrow="Your plan summary" className="mt-2">
            <div className="space-y-1.5 text-sm leading-relaxed text-slate-700">
                {integration && (
                    <p>
                        Under the linked plan ("{integration.name}"),{' '}
                        <span className="font-semibold text-slate-900">{integration.personName}</span> moves into
                        residential care:{' '}
                        {integration.radPaid > 0 && (
                            <>
                                <span className="font-semibold text-slate-900">{formatCurrency(integration.radPaid)}</span> is
                                {/* Not a bare "refundable later": the provider retains 2% a year of the balance for
                                    the first five years, which radPlugin.ts models and which the aged care recap
                                    already states. Same fee, so the same sentence about it. */}
                                paid as a <JargonTerm term="RAD" /> — returned when care ends, less the 2% a year the
                                provider retains for the first five years, and not counted for the pension —
                                and{' '}
                            </>
                        )}
                        <span className="font-semibold text-slate-900">{formatCurrency(integration.annualFees)}</span> a year
                        of aged care fees is paid from the household's cashflow.
                        {integration.illnessSeparated && (
                            <>
                                {' '}Once in care, Centrelink assesses the two of them as an{' '}
                                <JargonTerm term="illness-separated couple" /> — each paid at the higher single rate, with
                                more generous means-test thresholds. That reassessment is usually the biggest driver of the
                                pension change below.
                            </>
                        )}
                    </p>
                )}
                {levers.length > 0 && (
                    <p>
                        {integration ? 'On top of that, this plan is ' : 'This plan is '}
                        {levers.map((lever, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && (i === levers.length - 1 ? '; and ' : '; ')}
                                {lever}
                            </React.Fragment>
                        ))}
                        .
                    </p>
                )}
                {annuitySelected && <EngineAnnuityTerms terms={annuityTerms} factFind={factFind} />}
                {equityRelease && Number(equityRelease.loanAmount) > 0 && (
                    <EquityReleaseTerms
                        kind={equityRelease.kind === 'heas' ? 'heas' : 'reverseMortgage'}
                        loanAmount={Number(equityRelease.loanAmount) || 0}
                        interestRate={Number(equityRelease.interestRate) || null}
                        projectedBalance={Number(equityRelease.projectedBalance) || null}
                        years={Number(equityRelease.years) || null}
                        homeValue={Number(factFind?.home?.value) || null}
                    />
                )}
                {ratesMoved && ratesFell && (
                    <p>
                        Because the means assessment now sees less, the contribution rates fall — independence{' '}
                        {pct(ratesBefore?.independence)} → <span className="font-semibold text-slate-900">{pct(ratesAfter?.independence)}</span>,
                        everyday living {pct(ratesBefore?.everydayLiving)} →{' '}
                        <span className="font-semibold text-slate-900">{pct(ratesAfter?.everydayLiving)}</span> — and every
                        hour of service costs the household less.
                    </p>
                )}
            </div>
            <div className="mt-3 border-t border-slate-100 pt-3">
                <ShiftRow variant="expanded" label="Age Pension" before={pensionBefore} after={pensionAfter} why={pensionWhy} />
                <ShiftRow variant="expanded"
                    label="Support at Home contribution"
                    before={contributionBefore}
                    after={contributionAfter}
                    preferLower why={contributionWhy} />
                <ShiftRow variant="expanded" label="Yearly cashflow" before={before.cashFlow ?? 0} after={after.cashFlow ?? 0} why={cashflowWhy} />
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
                "Before" is doing nothing; "after" matches the contribution panel and Financial Impact figures alongside.
            </p>
        </PlainWordsCard>
        <PlanPiecesCard className="mt-4" pieces={planPieces} />
        </>
    );
};

export default SahPlanRecap;
